/**
 * Client-side deterministic AES-256-GCM encrypt/decrypt.
 * Uses Web Crypto API — works in browser, extension, and service worker contexts.
 *
 * IV is derived via HMAC-SHA256(key, plaintext).slice(0, 12), so identical
 * plaintext + same key always produces identical ciphertext. Per-user keys
 * from HKDF silo equality across users.
 *
 * `as BufferSource` casts: TS5.9 Uint8Array<ArrayBufferLike> strictness.
 */

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUint8(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function deriveIV(keyBytes: Uint8Array, plaintext: Uint8Array): Promise<Uint8Array> {
  const hmacKey = await crypto.subtle.importKey(
    'raw',
    keyBytes as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const mac = await crypto.subtle.sign('HMAC', hmacKey, plaintext as BufferSource);
  return new Uint8Array(mac).slice(0, 12);
}

/**
 * Deterministic AES-256-GCM encrypt.
 * Returns base64(12-byte IV || ciphertext || 16-byte auth tag).
 */
export async function encryptData(plaintext: string, base64Key: string): Promise<string> {
  const keyBytes = base64ToUint8(base64Key);
  const plaintextBytes = encoder.encode(plaintext);

  const iv = await deriveIV(keyBytes, plaintextBytes);
  const aesKey = await crypto.subtle.importKey('raw', keyBytes as BufferSource, 'AES-GCM', false, [
    'encrypt',
  ]);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    aesKey,
    plaintextBytes as BufferSource
  );

  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return uint8ToBase64(combined);
}

/**
 * AES-256-GCM decrypt.
 * Expects base64(12-byte IV || ciphertext || 16-byte auth tag).
 */
export async function decryptData(ciphertextBase64: string, base64Key: string): Promise<string> {
  const data = base64ToUint8(ciphertextBase64);
  const aesKey = await crypto.subtle.importKey(
    'raw',
    base64ToUint8(base64Key) as BufferSource,
    'AES-GCM',
    false,
    ['decrypt']
  );

  const iv = data.slice(0, 12);
  const ciphertext = data.slice(12);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    aesKey,
    ciphertext as BufferSource
  );

  return decoder.decode(plaintext);
}
